var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { ChipAction, useToast, Chip, Menu } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { DocumentMetadataPropertySetterEnum } from 'shared/domains/apollo/generated/types';
import { ChipDropdown } from 'shared/domains/common-ui';
import { MetadataFieldContext } from './MetadataFieldContext';
import { MetadataForm } from './MetadataForm';
import { FilesystemEntriesField } from './MetadataForm/FilesystemEntriesField';
import { FormField } from './MetadataForm/FormField';
import { MetadataReasoning } from './MetadataReasoning';
import { useMetadataActions } from '../../hooks/useMetadataActions';
import { useDocumentMetadata } from '../DocumentMetadataProvider';
import { getMetadataDisplayValue } from '../getMetadataDisplayValue';
export function MetadataField(props) {
    var _a, _b;
    const { documentId, pair, property } = props;
    const { toggleActiveEntry } = useDocumentMetadata();
    const actions = useMetadataActions();
    const { addToast } = useToast();
    const contextValues = {
        documentId,
        property,
        pair,
    };
    const defaultEditMode = !pair.id && !pair.value;
    const [open, setOpen] = useState(defaultEditMode);
    const needsVerification = pair.lastSetBy === DocumentMetadataPropertySetterEnum.Ai && !pair.approvedByUserId;
    const displayReasoning = pair.lastSetBy === DocumentMetadataPropertySetterEnum.Ai;
    const displayFilesystemEntry = property.type === 'reference' && property.reference === 'fileSystemEntry';
    const { label } = property;
    const [showReasoningDialog, setShowReasoningDialog] = useState(false);
    const openMenu = () => setOpen(true);
    const closeMenu = () => setOpen(false);
    const handleShowReasoningDialog = () => {
        setShowReasoningDialog(true);
    };
    const handleRemove = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield actions.delete(pair.id);
            addToast({
                title: t({
                    message: 'Field removed',
                    comment: 'Success toast when metadata field is removed',
                }),
                appearance: 'success',
            });
            toggleActiveEntry(property.name);
        }
        catch (_c) {
            addToast({
                title: t({ message: 'Error', comment: 'Metadata: Remove error title' }),
                message: t({
                    message: 'There was a error removing the field',
                    comment: 'Metadata: Remove error message',
                }),
                appearance: 'danger',
            });
        }
    });
    const handleSave = (form) => __awaiter(this, void 0, void 0, function* () {
        try {
            const formValue = form.value;
            const shouldUpdate = form.id;
            if (shouldUpdate) {
                yield actions.update({
                    id: pair.id,
                    value: formValue,
                });
            }
            else {
                yield actions.create({
                    propertyName: property.name,
                    value: formValue,
                    documentId,
                });
            }
            closeMenu();
            addToast({
                title: t({
                    message: 'Metadata updated',
                    comment: 'Success toast when metadata is created/updated',
                }),
                appearance: 'success',
            });
        }
        catch (error) {
            addToast({
                title: t({
                    message: 'An problem occurred',
                    comment: 'Error toast when metadata is created/updated',
                }),
                appearance: 'danger',
            });
        }
    });
    const handleVerify = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield actions.verify({
                pairIds: [pair.id],
            });
            addToast({
                title: t({
                    message: 'Field verified',
                    comment: 'Success toast when metadata field is verified',
                }),
                appearance: 'success',
            });
        }
        catch (_d) {
            addToast({
                title: t({ message: 'Error', comment: 'Metadata: Verify error title' }),
                message: t({
                    message: 'There was a error verifying the field',
                    comment: 'Metadata: Verify error message',
                }),
                appearance: 'danger',
            });
        }
    });
    if (!property || !label) {
        return (<Chip label={t({ message: 'Unknown property', comment: 'Metadata: Unknown property' })} appearance="warning" size="large"/>);
    }
    const chipAppearance = needsVerification ? 'assistant' : 'secondary';
    const chipSecondary = getMetadataDisplayValue({ property, value: pair.value });
    const chipAction = pair.id ? (<Menu.Root target={<ChipAction disabled={actions.loading} aria-label={t({
                message: 'Edit field',
                comment: 'Metadata: Button to display the different editing capabilities of a field.',
            })}>
          {actions.loading ? <Falcon icon="spinner"/> : <Falcon icon="ellipsis-vertical"/>}
        </ChipAction>}>
      {needsVerification && (<Menu.Item onClick={handleVerify} disabled={actions.loading || !pair.id}>
          <Falcon icon="shield-check"/>
          <Trans comment="Metadata: Remove metadata field">Verify</Trans>
        </Menu.Item>)}
      {displayReasoning && (<Menu.Item onClick={handleShowReasoningDialog}>
          <Falcon icon="lightbulb"/>
          <Trans comment="Metadata: Show reasoning">Reasoning</Trans>
        </Menu.Item>)}
      <Menu.Item onClick={handleRemove} disabled={actions.loading || !pair.id} data-testid="metadata-field-remove-id">
        <Falcon icon="trash"/>
        <Trans comment="Metadata: Remove metadata field">Remove</Trans>
      </Menu.Item>
    </Menu.Root>) : null;
    return (<MetadataFieldContext.Provider value={contextValues}>
      {showReasoningDialog && (<MetadataReasoning reasoning={(_a = pair.aiAutoTaggingReasoning) === null || _a === void 0 ? void 0 : _a.reasoning} citation={(_b = pair.aiAutoTaggingReasoning) === null || _b === void 0 ? void 0 : _b.citation} onClose={() => setShowReasoningDialog(false)}/>)}

      {displayFilesystemEntry && (<MetadataForm onSubmit={handleSave}>
          <FilesystemEntriesField label={label} secondary={chipSecondary} appearance={chipAppearance} chipAction={chipAction} initiallyOpen={defaultEditMode}/>
        </MetadataForm>)}

      {!displayFilesystemEntry && (<ChipDropdown size="medium" open={open} label={label} secondary={chipSecondary} appearance={chipAppearance} onClose={closeMenu} onOpen={openMenu} action={chipAction}>
          <MetadataForm onSubmit={handleSave}>
            <FormField />
          </MetadataForm>
        </ChipDropdown>)}
    </MetadataFieldContext.Provider>);
}
